/**
Copyright (C) 2019-2022, Infraclear Inc. All Rights Reserved.
*/

import React, { useEffect } from "react";
import "./dataTable.scss";
import { useState } from "react";
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import { ActivateUser, ArchiveUser, AssignUserRoles, CancelDeactivationRequest, ChangeTeam, DeactivateSubscription, EnableUser, EnableUserAndAssignRole, EnableUserTracker, ForceResetUser, GetUserRoles, PostAllUsersAPI, RejectUsersAPI, userLockReset } from "../../constants/api-urlConstants";
import instance from "../../utils/axiosInstance";
import { MessageText } from "../../constants/messageText";
import { ConfirmationBox } from "../ConfirmationBox/ConfirmationBox";
import { AssignRoles } from "../rolesAssign/assignRoles";
import { Box, Button, Checkbox, CircularProgress, Tooltip, FormControl, FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem, Radio, RadioGroup, Select } from "@mui/material";
import { SnackBarMessage } from "../snackBar/snackBar";
import { apiBasePath, apiBasePathSearch, apiBasePathUserDetail } from "../../constants/env-url";
import { RolePersmission } from "../../context/RolePermissionContext";
import { FEATURE_NAME, PermissionKey } from "../../constants/commonConstants";
import { ErrorCodes } from "../../constants/messageCodes";
import { NoRowsOverlay, StripedDataGrid, userColumns } from "./constantsForDataTable";
import SubscribeUserDetails from "../subscribeUserDeatilsPopup/subscribeDeatilsPopup";
import { TimerValusSet } from "../../context/TimerContext";
import { Modal } from "../Modal";
import { UpdateTrialDays } from "../updateTrialDays";
import {
  GridCallbackDetails, GridColDef, GridFilterInputMultipleSingleSelectProps,
  GridFilterInputMultipleValueProps, GridFilterInputValueProps, GridFilterItem,
  GridFilterModel, GridFilterOperator, getGridStringOperators
} from "@mui/x-data-grid";
import { YesOrNoDropdownOptions, availabelStatus } from "../../utils/constant";
import { UpdateExpiryDay } from "../updateExpiryDay";
import dayjs from "dayjs";
import FeaturePopUp from "../FeaturePopUp/FeaturePopUp";
import { isPayGAdmin } from "../../utils";

const SUBSCRIPTION_STATUS_MAP: Record<string, { label: string; color: "success" | "error" }> = {
  SUBSCRIBED: { label: "Subscribed", color: "success" },
  EXPIRED: { label: "Expired", color: "error" },
  TRIAL: { label: "Trial", color: "success" },
  TRIAL_EXPIRED: { label: "Trial Expired", color: "error" },
  DEACTIVATED: { label: "Deactivated", color: "error" },
};

const SUBSCRIPTION_STATUS_FILTER_OPTIONS = Object.entries(SUBSCRIPTION_STATUS_MAP).map(([value, { label }]) => ({
  value,
  label,
}));

export const DataTable: React.FC<any> = ({ searchParamValue, setAppliedFilters }) => {

  const { setUserHeartBeat, timeExit } = React.useContext(TimerValusSet);
  const [showUserDetails, setShowUserDetails] = React.useState(false);
  const [showAssignRoles, setShowAssignRoles] = React.useState(false);
  const [showConfirmation, setShowConfirmation] = React.useState(false);
  const [approveUser, setApproveUser] = React.useState(false);
  const [dataIsLoading, setDataIsLoading] = React.useState(false);
  const { permissionsObj } = React.useContext(RolePersmission);
  const [selectedRow, setSelectedRow] = React.useState<any>({});
  const [showEditTrialDays, setShowEditTrialDays] = React.useState(false);
  const [showEditExpiryDay, setShowEditExpiryDay] = React.useState(false);
  const [rolesList, setRoles] = React.useState<any>([]);
  const [msgConfig, setMsgConfig] = React.useState({
    popupTitle: '',
    popupDesc: ''
  });
  const [mfa2FaStatus, setMfa2FaStatus] = React.useState<{ isTwofaEnabled: boolean, isMfaEnabled: boolean }>({ isMfaEnabled: false, isTwofaEnabled: true });
  const [APIMsgCongig, setAPIMsgCongig] = React.useState({
    type: '',
    message: '',
    snackFlag: false
  });
  const [selectedID, setSelectedID] = React.useState<number>();
  const [showDeactivateConfirmation, setShowDeactivateConfirmation] = React.useState(false);
  const [deactivateEndOfSubscription, setDeactivateEndOfSubscription] = React.useState(false);
  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 50,
    roles: [],
    company: '',
    email: '',
    status: [],
    referredUser: null,
    teamName: '',
    subscriptionStatuses: [] as string[]
  });

  const [rolesAssignData, setRolesAssignData] = useState<any>({
    roleIsLoading: false,
    roleData: [],
    userID: '',
    team: {},
    expirationDate: ""
  });

  useEffect(() => {
    getRoles();
  }, []);

  const getRoles = () => {
    const USER_API_BASE_URL = `${GetUserRoles}`;
    instance
      .get(USER_API_BASE_URL)
      .then((response) => {
        const res = response.data;
        setUserHeartBeat(timeExit());
        if (res.success) {
          res.data.forEach((item: any) => {
            item.isDisabled = false;
          });
          setRoles(res.data);
        }
      })
      .catch(() => { });
  }

  const handleTrackerEnable = (userId: number, isEnable: boolean) => {
    setPageState(old => ({ ...old, isLoading: true }));
    const url = `${apiBasePath}${EnableUserTracker}${userId}`;
    const payload = {
      userTrackEnabled: isEnable
    };
    instance.post(url, payload).then((response) => {
      if (response?.data?.data) {
        setAPIMsgCongig({
          type: 'success',
          message: `User tracker has been ${isEnable ? 'enabled' : 'disabled'} successfully.`,
          snackFlag: true
        });
        getAllUsers();
      } else {
        setAPIMsgCongig({
          type: 'error',
          message: `Unable to ${isEnable ? 'enable' : 'disable'} user tracker.`,
          snackFlag: true
        });
        setPageState(old => ({ ...old, isLoading: false }));
      }
    }).catch(() => {
      setAPIMsgCongig({
        type: 'error',
        message: `Unable to ${isEnable ? 'enable' : 'disable'} user tracker.`,
        snackFlag: true
      })
    });
  };

  const actionColumn: any = [
    {
      field: "expiryDate",
      headerName: "Expiry date",
      minWidth: 180,
      flex: 1,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.expiryDate ? params.row.expiryDate : ""}>
            <div className="ellipsesAdd">
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                <span>{params.row.expiryDate ? params.row.expiryDate : "--"}</span>
                {params?.row?.roles?.toLocaleLowerCase?.().includes?.('contracts subscriber') &&
                  <IconButton
                    onClick={() => {
                      setSelectedRow(params.row);
                      setShowEditExpiryDay(true)
                    }}
                  >
                    <ModeEditIcon style={{ color: "#1976D2", height: '20px', width: '20px' }} />
                  </IconButton>
                }
              </Box>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "createdDate",
      headerName: "Created date",
      minWidth: 180,
      flex: 1,
      pinnable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.createdDate ? params.row.createdDate : ""}>
            <div className="ellipsesAdd" >
              {params.row.createdDate ? params.row.createdDate : "--"}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "lastLoginDate",
      headerName: "Last login date",
      minWidth: 140,
      flex: 1,
      pinnable: false,
      filterable: false,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.lastLoginDate ? params.row.lastLoginDate : ""}>
            <div className="ellipsesAdd" >
              {params.row.lastLoginDate ? params.row.lastLoginDate : "--"}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "subscriptionRequested",
      headerName: "Subscription Requested",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,

      renderCell: (params: any) => {
        return (

          <div className="viewAction">
            <Button
              disabled={!params.row.subscriptionRequested}
              variant="outlined"
              color="secondary"
              className="assignButton"
              onClick={() => openUserDetails(params.row.id)}
            >
              {params?.row?.subscriptionRequestStatus === "REJECTED" ? 'Review' : 'View'}
            </Button>
          </div>

        );
      },
    },
    {
      field: "subscriptionStatus",
      headerName: "Subscription Status",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: true,
      renderCell: (params: any) => {
        const status = params?.row?.subscriptionStatus;
        const statusInfo = status ? SUBSCRIPTION_STATUS_MAP[status] : null;
        return (
          <div className="viewAction">
            {statusInfo ? (
              <Button
                disableRipple
                variant="outlined"
                color={statusInfo.color}
                sx={{
                  px: "5px",
                  py: 0,
                  borderRadius: '5px',
                  border: '1px dotted',
                  textTransform: 'none',
                  cursor: 'text',
                  "&:hover": {
                    backgroundColor: "transparent",
                    border: '1px dotted'
                  },
                }}
              >
                {statusInfo.label}
              </Button>
            ) : '--'}
          </div>
        );
      },
    },
    {
      field: "autoRenewal",
      headerName: "Auto Renewal",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      renderCell: (params: any) => {
        const autoSubscription = params?.row?.autoSubscription;
        if (autoSubscription === null || autoSubscription === undefined) {
          return <div className="viewAction">--</div>;
        }
        return (
          <div className="viewAction">
            <Button
              disableRipple
              variant="outlined"
              color={autoSubscription ? "success" : "error"}
              sx={{
                px: "5px",
                py: 0,
                borderRadius: '5px',
                border: '1px dotted',
                textTransform: 'none',
                cursor: 'text',
                "&:hover": {
                  backgroundColor: "transparent",
                  border: '1px dotted',
                },
              }}
            >
              {autoSubscription ? "Enabled" : "Disabled"}
            </Button>
          </div>
        );
      },
    },
    {
      field: "nextBillingDate",
      headerName: "Expiration Date",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      renderCell: (params: any) => {
        const status = params?.row?.subscriptionStatus;
        const nextBillingDate = params?.row?.nextBillingDate;

        return nextBillingDate ? (
          <div className="viewAction">
            <Box className={status === "EXPIRED" || status === "TRIAL_EXPIRED" ? "expired" : ""}>
              {nextBillingDate}
            </Box>
          </div>
        ) : (
          <span>--</span>
        );
      },
    },
    {
      field: "trialDays",
      headerName: "Total trial days",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,

      renderCell: (params: any) => {
        return (
          <div className="viewAction cellAction">
            {(params.row.userType || params.row.status === "VALIDATION_PENDING") ?
              <>
                <span>{params.row.trialDays}</span>
                <IconButton
                  disabled={!(params.row.userType || params.row.status === "VALIDATION_PENDING")}
                  onClick={() => {
                    setSelectedRow(params.row);
                    setShowEditTrialDays(true)
                  }}
                  sx={{ p: 0 }}
                >
                  <ModeEditIcon style={{ color: "#1976D2", height: '20px', width: '20px' }} />
                </IconButton>
              </>
              : <span>--</span>
            }
          </div>
        );
      },
    },
    {
      field: "buyCredit",
      headerName: "Download Credits",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      renderCell: (params: any) => {
        return <span>{params.row.buyCredit || '--'}</span>
      }
    },
    {
      field: "rentCredit",
      headerName: "Rental Credits",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      renderCell: (params: any) => {
        return <span>{params.row.rentCredit || '--'}</span>
      }
    },
    {
      field: "trialRentCredit",
      headerName: "Free Rental Credits",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      align: "center",
      renderCell: (params: any) => {
        return (
          <div className="viewAction">
            {isPayGAdmin(params?.row?.roles) ? (
              <FeaturePopUp
                userId={params?.row?.id}
                value={params?.row?.trialRentCredit}
                featureName={FEATURE_NAME.FREE_RENTAL_CREDIT}
                onCloseCallBack={getAllUsers}
              />
            ) : "--"}
          </div>
        );
      }
    },
    {
      field: "trialBuyCredit",
      headerName: "Free Downloadable Credits",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      align: "center",
      renderCell: (params: any) => {
        return (
          <div className="viewAction">
            {isPayGAdmin(params?.row?.roles) ? (
              <FeaturePopUp
                userId={params?.row?.id}
                value={params?.row?.trialBuyCredit}
                featureName={FEATURE_NAME.FREE_BUY_CREDIT}
                onCloseCallBack={getAllUsers}
              />
            ) : "--"}
          </div>
        );
      }
    },
    {
      field: "freeChatCredit",
      headerName: "Free Chat Credits",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      align: "center",
      renderCell: (params: any) => {
        return (
          <div className="viewAction">
            {isPayGAdmin(params?.row?.roles) ? (
              <FeaturePopUp
                userId={params?.row?.id}
                value={params?.row?.freeChatCredit}
                featureName={FEATURE_NAME.FREE_CHAT_CREDIT}
                onCloseCallBack={getAllUsers}
              />
            ) : "--"}
          </div>
        );
      }
    },
    {
      field: "chatLimit",
      headerName: "Chat Limit",
      minWidth: 150,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      align: "center",
      renderCell: (params: any) => {
        return (
          <FeaturePopUp
            userId={params?.row?.id}
            value={params?.row?.chatLimit}
            featureName={FEATURE_NAME.CHAT}
            onCloseCallBack={getAllUsers}
          />
        );
      }
    },
    {
      field: "userTrackEnabled",
      headerName: "User Tracker",
      minWidth: 110,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,
      align: "center",
      renderCell: (params: any) => {
        return (
          <Box>
            <Checkbox
              checked={params?.row?.userTrackEnabled}
              onChange={(e) => {
                handleTrackerEnable(params?.row?.id, e.target.checked);
              }} />
          </Box>
        );
      }
    },
    {
      field: "action",
      headerName: "Actions",
      minWidth: 450,
      flex: 1,
      pinnable: false,
      disableSelectionOnClick: true,
      filterable: false,

      renderCell: (params: any) => {

        let buttonName: any;
        switch (params.row.status) {
          case 'ACTIVE': buttonName = 'Disable';
            break;
          case 'ONBOARDED': buttonName = 'Disable';
            break;
          case 'VALIDATION_PENDING': buttonName = 'Approve';
            break;
          case 'DECLINED': buttonName = 'Approve';
            break;
          case 'EXPIRED': buttonName = 'Active';
            break;
          case 'ARCHIVED': buttonName = 'Enable';
            break;
          default: buttonName = 'Approve';
        }
        if (params.row.blocked) {
          buttonName = 'Reactivate';
        }
        return (

          <Box className='cellAction'>
            <Button
              disabled={params.row.status === "DECLINED" || params.row.status === "ARCHIVED" || params.row.blocked}
              variant="outlined"
              className="assignButton"
              onClick={() => openRoles(params.row.id)}
            >
              Assign Roles
            </Button>

            <Button
              color={params.row.blocked ? "success" : ((params.row.status === "ACTIVE" || params.row.status === "ONBOARDED") ? "error" : "success")}
              variant="outlined"
              className="deleteButton"
              onClick={() => { params.row.status === "DECLINED" ? approveUserAction(params.row) : showConfirmationPopup(params.row) }}
            >
              {
                buttonName
              }
            </Button>
            <Button
              variant="outlined"
              className="assignButton"
              color="error"
              onClick={() => showDeactivateConfirmationPopup(params.row)}
              disabled={!(params?.row?.teamName && params.row.status === "ACTIVE" && params.row.nextBillingDate && !(params?.row?.subscriptionStatus === "DEACTIVATED"))}
            >
              Deactivate
            </Button>
            <Button
              disabled={params.row.status === "ARCHIVED" || params.row.status === "EXPIRED" || params.row.status === "DECLINED" || params.row.blocked}
              color="error"
              variant="outlined"
              className="assignButton"
              onClick={() => {
                (params.row.status === "ACTIVE" || params.row.status === "ONBOARDED") ? forceChangePasswordAction(params.row) : rejectUserAction(params.row)
              }}
              sx={{ width: '130px !important' }}
            >
              {
                (params.row.status === "ACTIVE" || params.row.status === "ONBOARDED") ? "Resend Password" : "Reject"
              }
            </Button>
          </Box>
        );
      },
    },
  ];

  const onCloseTrialDaysPopup = () => {
    setSelectedRow({});
    setShowEditTrialDays(false);
    setShowConfirmation(false);
    setApproveUser(false);
    setShowEditExpiryDay(false);
  }

  const onSaveAndCloseTrialDaysPopup = () => {
    onCloseTrialDaysPopup();
    getAllUsers();
  }

  const getAllUsers = (roles = [], company = '', status = [], referredUser = null, email = '', teamName = '', isOnFilter = false, subscriptionStatuses: string[] = []) => {
    const updatedFilters = {
      roles: isOnFilter || roles.length ? roles : pageState.roles,
      companyName: isOnFilter || company ? company : pageState.company,
      email: isOnFilter || email ? email : pageState.email,
      status: isOnFilter || status.length ? status : pageState.status,
      referredUser: isOnFilter || referredUser !== null ? referredUser : pageState.referredUser,
      teamName: isOnFilter || teamName ? teamName : pageState.teamName,
      subscriptionStatuses: isOnFilter || subscriptionStatuses.length ? subscriptionStatuses : pageState.subscriptionStatuses,
    }
    setPageState(old => ({
      ...old,
      isLoading: true,
      ...updatedFilters,
    }));
    const Payload = {
      name: searchParamValue,
      page: pageState.page,
      size: pageState.pageSize,
      ...updatedFilters,
    }
    setAppliedFilters(updatedFilters);
    let USER_API_BASE_URL = `${apiBasePathUserDetail}${PostAllUsersAPI}`;
    instance
      .post(USER_API_BASE_URL, Payload)
      .then((response) => {
        setUserHeartBeat(timeExit());
        if (response.data.success) {
          setDataIsLoading(true);
          const res = response.data.data;
          setPageState(old => ({ ...old, isLoading: false, data: res.dataList, total: res.totalRecords }));
        }
        else if (response.data.message_code === ErrorCodes.USER_EXPIRED) {
          setAPIMsgCongig({
            type: 'error',
            message: MessageText.INTERNAL_SERVER_ERROR,
            snackFlag: pageState.isLoading
          });
          setDataIsLoading(true);
          setPageState(old => ({ ...old, isLoading: false }))
        } else {
          setDataIsLoading(true);
          setPageState(old => ({ ...old, isLoading: false }))
        }
      })
      .catch((error) => {
      });

  };

  const setMsgText = (row: any) => {

    switch (row.status) {
      case "ACTIVE": case "ONBOARDED":
        setMsgConfig({
          popupTitle: MessageText.USER_STATUS_DISABLE,
          popupDesc: MessageText.ACRHIVE_USER
        });
        break;
      case "VALIDATION_PENDING":
        setMsgConfig({
          popupTitle: MessageText.USER_STATUS_APPROVE,
          popupDesc: MessageText.ACTIVATE_USER
        });
        break;
      case "ARCHIVED":
        setMsgConfig({
          popupTitle: MessageText.USER_STATUS_ENABLE,
          popupDesc: MessageText.ENABLE_USER
        });
        break;
      case "DECLINED":
        setMsgConfig({
          popupTitle: MessageText.USER_STATUS_REJECT,
          popupDesc: MessageText.REJECT_USER_CONFORM
        });
        break;
      case "EXPIRED":
        setMsgConfig({
          popupTitle: MessageText.USER_STATUS_ACTIVE,
          popupDesc: MessageText.ACTIVATE_USER
        });
        break;

      case "FORCE_CHANGED_PASSWORD":
        setMsgConfig({
          popupTitle: MessageText.USER_RESEND_PASSWORD,
          popupDesc: MessageText.RESEND_PASSWORD
        });
        break;
    }

    if (row.blocked) {
      setMsgConfig({
        popupTitle: MessageText.REACTIVATE_USER,
        popupDesc: MessageText.REACTIVATE_USER_DESC
      });
    }
  };

  const changeUserStatus = (row: any) => {

    if (row.blocked) {
      const payload_reactivateUser = {
        id: row.id
      }
      reactivateUser(payload_reactivateUser);
    } else {
      switch (row.status) {
        case "ACTIVE": case "ONBOARDED":
          const payload = {
            id: row.id
          }
          changeUserState(payload, row, ArchiveUser, 'ARCHIVED', MessageText.ARCHIVE_SUCCESS, 'success');
          break;
        case "VALIDATION_PENDING":
          const payload_pending = {
            "email": row.email,
            "id": row.id,
            isMfaEnabled: mfa2FaStatus.isMfaEnabled,
            isTwofaEnabled: mfa2FaStatus.isTwofaEnabled,
          }
          changeUserState(payload_pending, row, ActivateUser, 'ACTIVE', MessageText.USER_ACTIVATED, 'success')
          break;
        case "ARCHIVED":
          const payload_archived = {
            id: row.id
          }
          changeUserState(payload_archived, row, EnableUser, 'ACTIVE', MessageText.USER_ENABLED, 'success')
          break;
        case "DECLINED":
          const payload_reject = {
            id: row.id
          }
          changeUserState(payload_reject, row, RejectUsersAPI, 'DECLINED', MessageText.REJECT_USER_SUCCESS, 'error')
          break;
        case "EXPIRED":
          const payload_expire = {
            id: row.id
          }
          changeUserState(payload_expire, row, EnableUser, 'ACTIVE', MessageText.USER_ACTIVATED, 'success')
          break;

        case "FORCE_CHANGED_PASSWORD":
          const payload_resetUser = {
            id: row.id
          }
          changeUserState(payload_resetUser, row, ForceResetUser, 'ACTIVE', MessageText.VALID_USER_STATUS, 'success')
          break;
      }
    }
  };

  const reactivateUser = (payload: { id: string }) => {
    const apiUrl = `${apiBasePath}${userLockReset}`;
    instance.post(apiUrl, payload).then((res) => {
      if (res.data.success) {
        setAPIMsgCongig({
          type: 'success',
          message: MessageText.REACTIVATED_SUCCESSFULLY,
          snackFlag: true
        });
        setSelectedRow({});
        getAllUsers();
      }
    }).catch(() => {
      setAPIMsgCongig({
        type: 'error',
        message: MessageText.WENT_WRONG,
        snackFlag: true
      });
    });

  }

  const showConfirmationPopup = (row: any) => {
    setShowConfirmation(true);
    if ((row.status === 'VALIDATION_PENDING' || row.status === 'DECLINED') && (row.roles === '' || row.roles === 'Contracts Trial')) {
      setApproveUser(true);
      setShowConfirmation(false);
    }
    setMsgText(row);
    setSelectedRow(row);
  };

  const showDeactivateConfirmationPopup = (row: any) => {
    setDeactivateEndOfSubscription(!!row?.deactivationRequested);
    setSelectedRow(row);
    setShowDeactivateConfirmation(true);
  };

  const cancelDeactivationRequest = () => {
    const row = selectedRow;
    if (!row?.id) return;
    setPageState(old => ({ ...old, isLoading: true }));
    const url = `${apiBasePathSearch}${CancelDeactivationRequest}`;
    instance
      .post(url, null, { params: { id: row.id } })
      .then((response) => {
        setUserHeartBeat(timeExit());
        if (response?.data?.success === false) {
          setAPIMsgCongig({
            type: 'error',
            message: MessageText.CANCEL_DEACTIVATION_FAILED,
            snackFlag: true
          });
          setPageState(old => ({ ...old, isLoading: false }));
          return;
        }
        setAPIMsgCongig({
          type: 'success',
          message: MessageText.CANCEL_DEACTIVATION_SUCCESS,
          snackFlag: true
        });
        setShowDeactivateConfirmation(false);
        setSelectedRow({});
        getAllUsers();
        setPageState(old => ({ ...old, isLoading: false }));
      })
      .catch(() => {
        setAPIMsgCongig({
          type: 'error',
          message: MessageText.CANCEL_DEACTIVATION_FAILED,
          snackFlag: true
        });
        setPageState(old => ({ ...old, isLoading: false }));
      });
  };

  const onConfirmDeactivate = (confirmed: boolean) => {
    if (!confirmed) {
      setShowDeactivateConfirmation(false);
      setSelectedRow({});
      return;
    }
    const row = selectedRow;
    if (!row?.id) return;
    const url = `${apiBasePathSearch}${DeactivateSubscription}`;
    const payload = { userId: row.id, endOfSubscription: deactivateEndOfSubscription };
    setPageState(old => ({ ...old, isLoading: true }));
    instance
      .delete(url, { data: payload })
      .then((response) => {
        setUserHeartBeat(timeExit());
        const code = response?.data?.message_code;
        if (code === ErrorCodes.SUBSCRIPTION_DEACTIVATED) {
          setAPIMsgCongig({
            type: 'success',
            message: MessageText.DEACTIVATE_SUCCESS,
            snackFlag: true
          });
          setShowDeactivateConfirmation(false);
          setSelectedRow({});
          getAllUsers();
        } else if (code === ErrorCodes.DEACTIVATION_AT_END_REQUESTED) {
          setAPIMsgCongig({
            type: 'success',
            message: MessageText.DEACTIVATE_END_OF_SUBSCRIPTION_SUCCESS,
            snackFlag: true
          });
          setShowDeactivateConfirmation(false);
          setSelectedRow({});
          getAllUsers();
        } else if (code === ErrorCodes.SUBSCRIPTION_DEACTIVATION_ALREADY_REQUESTED) {
          setAPIMsgCongig({
            type: 'success',
            message: MessageText.DEACTIVATE_ALREADY_REQUESTED,
            snackFlag: true
          });
          setShowDeactivateConfirmation(false);
          setSelectedRow({});
          getAllUsers();
        } else {
          setAPIMsgCongig({
            type: 'error',
            message: MessageText.DEACTIVATE_FAILED,
            snackFlag: true
          });
        }
        setPageState(old => ({ ...old, isLoading: false }));
      }).catch((error) => {
        const errCode = error?.response?.data?.message_code;
        const errMessage =
          errCode === ErrorCodes.SUBSCRIPTION_CANCELLATION_FAILED
            ? MessageText.SUBSCRIPTION_CANCELLATION_FAILED
            : MessageText.DEACTIVATE_FAILED;
        setAPIMsgCongig({
          type: 'error',
          message: errMessage,
          snackFlag: true
        });
        setPageState(old => ({ ...old, isLoading: false }));
      }).finally(() => {
        setShowDeactivateConfirmation(false);
      });
  };

  const changeUserState = (payload: any, row: any, api_name: any, state_change_to: any, Message_text: any, Message_Type: any) => {
    const USER_API_BASE_URL = `${api_name}`;
    setPageState(old => ({ ...old, isLoading: true }));
    instance
      .post(USER_API_BASE_URL, payload)
      .then((response) => {
        setUserHeartBeat(timeExit());
        if (response.data.success) {
          row.status = state_change_to;
          setPageState(old => ({ ...old, isLoading: false, data: pageState.data }));
          setSelectedRow({});
          getAllUsers();
          setAPIMsgCongig({
            type: Message_Type,
            message: Message_text,
            snackFlag: true
          });
        }
        else if (api_name === ForceResetUser && !response.data.success) {

          setAPIMsgCongig({
            type: 'error',
            message: MessageText.INVALID_USER_STATUS,
            snackFlag: true
          });
          getAllUsers();
        }
        else {
          setAPIMsgCongig({
            type: 'error',
            message: MessageText.WENT_WRONG,
            snackFlag: true
          });

        }
      })
      .catch((error) => {
        setAPIMsgCongig({
          type: 'error',
          message: MessageText.WENT_WRONG,
          snackFlag: true
        });
      });
  };

  const openUserDetails = (id: any) => {
    setSelectedID(id)
    setShowUserDetails(true);
  };

  const openRoles = (id: number) => {
    setSelectedID(id)
    setShowAssignRoles(true);
  };
  const closeUserDetails = () => {
    setShowUserDetails(false);
  };

  const closeRoles = () => {
    setShowAssignRoles(false);
  };

  const showMsgOnAssignComplete = (showMSg: any) => {
    if (showMSg) {
      setAPIMsgCongig({
        type: 'success',
        message: MessageText.ROLES_ASSIGNED,
        snackFlag: true
      })
    }
  };

  const changeUserTeam = (payload: { id: number, teamId: number }) => {
    const CHANGE_TEAM_API = `${ChangeTeam}`;
    instance.post(CHANGE_TEAM_API, payload).then(() => { }).catch(() => { });
  }

  const assignRoles = (roleData: any, isExpiredUser: boolean = false) => {
    setPageState(old => ({ ...old, isLoading: true }));
    const payload = {
      "id": roleData.userID,
      "roles": roleData.roleData,
      mfaEnabled: mfa2FaStatus.isMfaEnabled,
      twofaEnabled: mfa2FaStatus.isTwofaEnabled,
      expirationDate: roleData.expirationDate ? dayjs(roleData.expirationDate).format("YYYY-MM-DD") : ""
    }
    roleData?.team?.id && changeUserTeam({ id: roleData.userID, teamId: roleData?.team?.id });
    const USER_API_BASE_URL = isExpiredUser ? `${EnableUserAndAssignRole}` : `${AssignUserRoles}`;

    instance
      .post(USER_API_BASE_URL, payload)
      .then((response) => {
        setUserHeartBeat(timeExit());
        const res = response.data;
        if (!res.success && res.message_code === ErrorCodes.INVALID_ROLE_CHANGE) {
          setPageState(old => ({ ...old, isLoading: false }));
          setAPIMsgCongig({
            type: 'error',
            message: MessageText.INVALID_ROLE_CHANGE_MESSAGE,
            snackFlag: true
          })
        }
        if (res.success) {
          closeRoles();
          setAPIMsgCongig({
            type: 'success',
            message: MessageText.ROLES_ASSIGNED,
            snackFlag: true
          });
          getAllUsers();
          setRolesAssignData((old: any) => ({ ...old, roleIsLoading: false, roleData: [], userID: '', team: {} }));
        }
      })
      .catch((error) => {
        setAPIMsgCongig({
          type: 'error',
          message: MessageText.WENT_WRONG,
          snackFlag: true
        })
      }).finally(() => {
        setMfa2FaStatus({ isMfaEnabled: false, isTwofaEnabled: true });
      });
  };

  const onConfirmAction = (value: any) => {
    if (value) {
      setShowConfirmation(false);
      changeUserStatus(selectedRow);
      const userId = rolesAssignData?.userID;
      const row: any = pageState?.data?.find((row: any) => row?.id === userId);
      const isExpiredUser = (row?.status === "EXPIRED");
      if (rolesAssignData.roleIsLoading) {
        assignRoles(rolesAssignData, isExpiredUser);
      }
    } else {
      setSelectedRow({});
      setShowConfirmation(false);
    }
  };

  const rejectUserAction = (row: any) => {
    setShowConfirmation(true);
    const rejectUser = {
      status: "DECLINED",
      id: row.id
    }
    setMsgText(rejectUser);
    setSelectedRow(rejectUser);
  };

  const approveUserAction = (row: any) => {
    setShowConfirmation(true);
    if ((row.status === 'VALIDATION_PENDING' || row.status === 'DECLINED') && ((row.roles === '' || row.roles === 'Contracts Trial'))) {
      setApproveUser(true);
      setShowConfirmation(false);
    }
    const approveUser = {
      ...row,
      status: "VALIDATION_PENDING",
    }
    setMsgText(approveUser);
    setSelectedRow(approveUser);
  };

  const forceChangePasswordAction = (row: any) => {
    setShowConfirmation(true);
    const forceChangePass = {
      status: "FORCE_CHANGED_PASSWORD",
      id: row.id
    }
    setMsgText(forceChangePass);
    setSelectedRow(forceChangePass);
  };

  useEffect(() => {
    getAllUsers();
  }, [pageState.page, pageState.pageSize, searchParamValue]);

  const addCustomOperatorForAllColumns = (columns: GridColDef[]) => {
    const containsOnlyOperator: any = getGridStringOperators().find(f => f.value === 'contains');
    const containsOnlyOperators: GridFilterOperator[] = [
      containsOnlyOperator
    ];
    columns.map((col) => {
      if (col.field === 'roles') {
        col.filterOperators = getFilterOperators(RolesDropdownFilter);
      } else if (col.field === 'status') {
        col.filterOperators = getFilterOperators(StatusDropdownFilter);
      } else if (col.field === 'subscriptionStatus') {
        col.filterOperators = getFilterOperators(SubscriptionStatusDropdownFilter);
      } else if (col.field === 'referredUser') {
        col.filterOperators = getFilterOperators(ReferredUserDropdownFilter);
      } else {
        col.filterOperators = containsOnlyOperators;
      }
      return col;
    })
    return columns;
  }

  const onFilterChange = (e: GridFilterModel, details: GridCallbackDetails<'filter'>) => {
    if (details.reason) {
      const roles = e.items.find((item) => item.field === "roles")?.value || [];
      const status = e.items.find((item) => item.field === "status")?.value || [];
      const company = e.items.find((item) => item.field === "company")?.value || "";
      const email = e.items.find((item) => item.field === "email")?.value || "";
      const referredUser = e.items.find((item) => item.field === "referredUser")?.value;
      const teamName = e.items.find((item) => item?.field === 'teamName')?.value || '';
      const subscriptionStatuses = (e.items.find((item) => item.field === 'subscriptionStatus')?.value as string[] | undefined) || [];
      const isReferredUser: any = YesOrNoDropdownOptions.find(option => option.label === referredUser)?.value;
      getAllUsers(roles, company, status, isReferredUser, email, teamName, true, subscriptionStatuses);
    }
  }

  const ReferredUserDropdownFilter = (props: GridFilterInputValueProps) => {
    const { item, applyValue } = props;
    const handleFilterChange = (event: any) => {
      const isReferredUser = event.target.value;
      applyValue({ ...item, value: isReferredUser });
    };

    return (
      <FormControl variant="standard" >
        <InputLabel id="filter-simple-select-standard-label">Value</InputLabel>
        <Select
          labelId="filter-multiple-checkbox-label"
          id="filter-multiple-checkbox-referredUser"
          className="filter-multiple-checkbox"
          name="referredUser"
          value={item?.value || ''}
          onChange={(e) => handleFilterChange(e)}
          renderValue={(value: any) => {
            return YesOrNoDropdownOptions.find(option => option.label === item?.value)?.label;
          }}
        >
          {YesOrNoDropdownOptions.map((status: any) => (
            <MenuItem key={status.label} value={status.label}>
              <Checkbox checked={status.label === item?.value} />
              <ListItemText primary={status.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const StatusDropdownFilter = (props: GridFilterInputValueProps) => {
    const { item, applyValue } = props;
    const handleFilterChange = (event: any) => {
      const updatedStatus = [...event.target.value];
      const filteredStatus = updatedStatus.filter((status, index) => updatedStatus.indexOf(status) === index)
      applyValue({ ...item, value: filteredStatus });
    };

    return (
      <FormControl fullWidth size="small">
        <InputLabel id="filter-simple-select-standard-label">Value</InputLabel>
        <Select
          labelId="filter-multiple-checkbox-label"
          id="filter-multiple-checkbox-status"
          className="filter-multiple-checkbox"
          multiple
          name="status"
          value={Array.isArray(item?.value) ? item?.value : []}
          onChange={(e) => handleFilterChange(e)}
          renderValue={(value: string[]) => {
            return availabelStatus?.filter(option => value?.includes(option.value))?.map(a => a?.label)?.join(', ');
          }}
          label="Value"
        >
          {availabelStatus.map((status: any) => (
            <MenuItem key={status.value} value={status.value}>
              <Checkbox checked={item?.value?.indexOf(status.value) > -1} />
              <ListItemText primary={status.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const RolesDropdownFilter = (props: GridFilterInputValueProps) => {
    const { item, applyValue } = props;
    const handleFilterChange = (event: any) => {
      const updatedRoles = [...event.target.value];
      const filteredRoles = updatedRoles.filter((role, index) => updatedRoles.indexOf(role) === index)
      applyValue({ ...item, value: filteredRoles });
    };

    return (
      <FormControl fullWidth size="small">
        <InputLabel id="filter-simple-select-standard-label">Value</InputLabel>
        <Select
          labelId="filter-multiple-checkbox-label"
          id="filter-multiple-checkbox"
          label="Value"
          multiple
          name="roles"
          value={Array.isArray(item?.value) ? item?.value : []}
          onChange={(e) => handleFilterChange(e)}
          renderValue={(value: any) => {
            let roleIds: any = []
            value.forEach((ele: any) => {
              rolesList.forEach((role: any) => {
                if (ele === role.id) {
                  roleIds.push(role.name.value)
                }
              })
            });
            return roleIds.join(', ')
          }}
        >
          {rolesList.length && rolesList.map((role: any) => (
            <MenuItem key={role.id} value={role.id} disabled={role.isDisabled}>
              <Checkbox checked={item?.value?.indexOf(role.id) > -1} />
              <ListItemText primary={role.name.value} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const SubscriptionStatusDropdownFilter = (props: GridFilterInputValueProps) => {
    const { item, applyValue } = props;
    const handleFilterChange = (event: any) => {
      const updated = [...event.target.value];
      const deduped = updated.filter((v, index) => updated.indexOf(v) === index);
      applyValue({ ...item, value: deduped });
    };

    return (
      <FormControl fullWidth size="small">
        <InputLabel id="filter-subscription-status-label">Value</InputLabel>
        <Select
          labelId="filter-subscription-status-checkbox-label"
          id="filter-multiple-checkbox-subscriptionStatus"
          label="Value"
          multiple
          name="subscriptionStatus"
          value={Array.isArray(item?.value) ? item?.value : []}
          onChange={(e) => handleFilterChange(e)}
          renderValue={(value: string[]) =>
            SUBSCRIPTION_STATUS_FILTER_OPTIONS.filter((opt) => value?.includes(opt.value))
              .map((opt) => opt.label)
              .join(', ')
          }
        >
          {SUBSCRIPTION_STATUS_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              <Checkbox checked={item?.value?.indexOf(opt.value) > -1} />
              <ListItemText primary={opt.label} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const getFilterOperators = (component: React.JSXElementConstructor<GridFilterInputValueProps> | React.JSXElementConstructor<GridFilterInputMultipleValueProps> | React.JSXElementConstructor<GridFilterInputMultipleSingleSelectProps>): GridFilterOperator[] => [
    {
      label: 'Contains',
      value: 'contains',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        return (params): boolean => {
          return Number(params.value) >= Number(filterItem.value);
        };
      },
      InputComponent: component,
    },
  ];

  return (
    <>
      <div className={`${pageState.isLoading ? 'disableTable datatable' : 'datatable'}`}>
        {dataIsLoading ?
          <StripedDataGrid
            className="datagrid"
            rows={pageState.data}
            rowCount={pageState.total}
            pageSizeOptions={[10, 20, 30, 50, 100]}
            loading={pageState.isLoading}
            pagination
            hideFooterSelectedRowCount
            paginationModel={{ page: pageState.page - 1, pageSize: pageState.pageSize }}
            paginationMode="server"
            filterMode="server"
            onFilterModelChange={onFilterChange}
            onPaginationModelChange={(model) => {
              setPageState(old => ({
                ...old,
                page: model.page + 1,
                pageSize: model.pageSize
              }))
            }}
            disableColumnSelector
            disableDensitySelector
            columns={addCustomOperatorForAllColumns(permissionsObj[PermissionKey.AUTH_ACTION_COLUMN_VIEW] ? userColumns.concat(actionColumn) : userColumns)}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            slots={{ noRowsOverlay: NoRowsOverlay }}
            initialState={{ pinnedColumns: { left: ['firstName', 'company'] } }}
          />
          :
          <div className="loadingScreen">
            <CircularProgress />
          </div>
        }
        {
          showUserDetails &&
          <div>
            <SubscribeUserDetails openSubscribePopup={showUserDetails} closeSubscribePopup={closeUserDetails} selectedUserID={selectedID} errorMessageShow={(value) => {
              if (value.message === MessageText.APPROVE_USER_SUCCESS || value.message === MessageText.REJECT_USER_SUCCESS) {
                getAllUsers();
              };
              setAPIMsgCongig(value);
            }} />
          </div>
        }

        {showAssignRoles &&
          <AssignRoles
            openPopup={showAssignRoles}
            hidePopup={closeRoles}
            showMsgOnAssign={(msg: any) => {
              showMsgOnAssignComplete(msg)
            }}
            confirmationFlag={(flag: any, name: any, mfa2FaStatus: { isTwofaEnabled: boolean, isMfaEnabled: boolean }, team: { id: number, name: string }, expirationDate: string) => {
              if (flag) {
                setMsgConfig({
                  popupTitle: MessageText.ASSIGN_ROLE,
                  popupDesc: MessageText.ASSIGN_ROLE_USER
                });
                setShowConfirmation(true);
                setShowAssignRoles(false);
                setRolesAssignData((old: any) => ({ ...old, roleIsLoading: true, roleData: name, userID: selectedID, team: team, expirationDate: expirationDate }));
                setMfa2FaStatus({ isMfaEnabled: mfa2FaStatus.isMfaEnabled, isTwofaEnabled: mfa2FaStatus.isTwofaEnabled });
              }
            }}
            selectedUserID={selectedID}
          />}
        {showConfirmation &&
          <ConfirmationBox
            openConfirmation={showConfirmation}
            closeConfirmation={onConfirmAction}
            msgConfig={msgConfig}
          >
            {selectedRow?.status === 'VALIDATION_PENDING' &&
              <Box className="descWrapper" sx={{ marginTop: 2 }}>
                <div className="titleDescConfirm">Authentication Mechanism</div>
                <RadioGroup
                  row
                  aria-labelledby="radio-buttons-mfa-2fa"
                  name="radio-buttons-mfa-2fa"
                  value={mfa2FaStatus.isMfaEnabled ? 'MFA' : mfa2FaStatus.isTwofaEnabled ? '2FA' : ''}
                  onChange={(e: any) => {
                    const value = (e.target.value);
                    const is2fa = value === '2FA';
                    const isMfa = value === 'MFA';
                    setMfa2FaStatus({
                      isMfaEnabled: isMfa,
                      isTwofaEnabled: is2fa
                    })
                  }}
                >
                  <FormControlLabel value="MFA" control={<Radio />} label="MFA" />
                  <FormControlLabel value="2FA" control={<Radio />} label="2FA" />
                </RadioGroup>
              </Box>
            }
          </ConfirmationBox>
        }
        {showDeactivateConfirmation &&
          <ConfirmationBox
            openConfirmation={showDeactivateConfirmation}
            closeConfirmation={onConfirmDeactivate}
            hidePrimaryButton={!!selectedRow?.deactivationRequested}
            msgConfig={{
              popupTitle: MessageText.DEACTIVATE_SUBSCRIPTION_TITLE,
              popupDesc: MessageText.DEACTIVATE_SUBSCRIPTION_DESC,
              primaryBtnTxt: 'Confirm',
              secondaryBtnTxt: 'Cancel'
            }}
            additionalFooterActions={
              selectedRow?.deactivationRequested ? (
                <Button
                  className="secondaryBtn"
                  sx={{ width: '150px !important', textTransform: 'none' }}
                  disabled={pageState.isLoading}
                  onClick={() => cancelDeactivationRequest()}
                >
                  {MessageText.CANCEL_DEACTIVATION_REQUEST}
                </Button>
              ) : null
            }
          >
            <Box sx={{ width: '400px', mt: '10px' }}>
              <RadioGroup
                aria-labelledby="deactivate-options"
                name="deactivate-options"
                value={deactivateEndOfSubscription ? 'endOfSubscription' : 'immediately'}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  if (selectedRow?.deactivationRequested) return;
                  setDeactivateEndOfSubscription(e.target.value === 'endOfSubscription');
                }}
              >
                <FormControlLabel
                  value="immediately"
                  control={<Radio />}
                  label={MessageText.DEACTIVATE_IMMEDIATELY}
                  disabled={!!selectedRow?.deactivationRequested}
                />
                <FormControlLabel
                  value="endOfSubscription"
                  control={<Radio />}
                  label={MessageText.DEACTIVATE_END_OF_SUBSCRIPTION}
                  disabled={selectedRow?.subscriptionStatus === 'EXPIRED'}
                />
              </RadioGroup>
            </Box>
          </ConfirmationBox>}
        {
          approveUser &&
          <Modal
            title="Approve User"
            open={approveUser}
            onClose={() => onCloseTrialDaysPopup()}
          >
            <UpdateTrialDays
              selectedUser={selectedRow}
              onCancel={() => onCloseTrialDaysPopup()}
              onSaveAndClose={() => onSaveAndCloseTrialDaysPopup()}
              buttonName="Approve"
              closeConfirmation={onConfirmAction}
            />
          </Modal>
        }
      </div>
      {APIMsgCongig.snackFlag && <div>
        <SnackBarMessage snacBarValue={APIMsgCongig} />
      </div>
      }
      <Modal
        title="Update Trial Days"
        open={showEditTrialDays}
        onClose={() => onCloseTrialDaysPopup()}
      >
        <UpdateTrialDays
          selectedUser={selectedRow}
          onCancel={() => onCloseTrialDaysPopup()}
          onSaveAndClose={() => onSaveAndCloseTrialDaysPopup()}
          buttonName="Save"
        />
      </Modal>
      <Modal
        title="Update Expiry Date"
        open={showEditExpiryDay}
        onClose={() => onCloseTrialDaysPopup()}
      >
        <UpdateExpiryDay
          selectedUser={selectedRow}
          onCancel={() => onCloseTrialDaysPopup()}
          onSaveAndClose={() => onSaveAndCloseTrialDaysPopup()}
          buttonName="Save"
        />
      </Modal>
    </>
  );
}